import type { PortalSiteDisplaySettingsDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { ReactElement } from 'react';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';

interface ProductMetaLinesProps {
	/**
	 * Selling-unit price. In this data model `item.price` IS the price per
	 * `unitOfMeasure` (order lines transact at it), so we surface it as the
	 * unit price labeled with its UOM rather than dividing by caseSize — see
	 * PD-13692. Null hides the unit-price line.
	 */
	price: string | null;
	/** Unit the price + stock are denominated in (e.g. "Capsule", "Case", "EA"). */
	unitOfMeasure: string | null;
	/** Available stock on hand, or null when hidden / not applicable. */
	quantityOnHand: number | null;
	/** Soonest lot expiration (ISO date), or null for non-lot-controlled items. */
	expirationDate: string | null;
	display: PortalSiteDisplaySettingsDto;
	className?: string;
}

/**
 * Compact secondary product facts shown under the price on the catalog grid,
 * list, and detail surfaces (PD-13692): the unit price (price + unit-of-measure
 * label), the quantity on hand, and the soonest lot expiration. Each line
 * self-gates on its own data plus the storefront's visibility prefs, and the
 * whole block renders nothing when all three are absent so card heights stay
 * tight. Mirrors the fields the legacy 1.0 shop exposed.
 */
export function ProductMetaLines({
	price,
	unitOfMeasure,
	quantityOnHand,
	expirationDate,
	display,
	className,
}: ProductMetaLinesProps): ReactElement | null {
	const { t } = useTranslation();

	const uom =
		unitOfMeasure != null && unitOfMeasure.trim() !== '' ? unitOfMeasure : null;
	// The price the visitor sees IS the per-UOM unit price, so we annotate it
	// with the unit rather than repeating the number — "$5.20" + "per Capsule".
	// Gated on CATALOG pricing (PD-14775) — this block only renders on
	// catalog/product surfaces.
	const showUnit =
		display.canSeeCatalogPricing &&
		formatCurrency(price) != null &&
		uom != null;
	const showQty = display.canSeeStockQuantity && quantityOnHand != null;
	const showExp = expirationDate != null;

	if (!showUnit && !showQty && !showExp) {
		return null;
	}

	return (
		<div
			className={`flex flex-col gap-0.5 text-[11px] leading-tight ${className ?? ''}`}
		>
			{showUnit && (
				<span className="text-brand-muted">
					{t('per')} {t(uom)}
				</span>
			)}
			{showQty && (
				<span className="text-brand-muted">
					{t(String(quantityOnHand))} {t('in stock')}
				</span>
			)}
			{showExp && (
				<span className="text-warning">
					{t('Exp')} {t(formatStatementDate(expirationDate))}
				</span>
			)}
		</div>
	);
}
